import React, { createContext, useContext, useMemo, useEffect, useState } from "react";
import { Auth, API } from "aws-amplify";

interface AuthConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId: string;
  oauth: {
    domain: string;
    scope: string[];
    redirectSignIn: string;
    redirectSignOut: string;
    responseType: string;
  };
}

interface AppSyncConfig {
  endpoint: string;
  region: string;
  defaultAuthMode: string;
}

const authConfigs: Record<string, AuthConfig> = {
  localhost: {
    region: "us-west-2",
    userPoolId: "us-west-2_eJNPRTj1y",
    userPoolWebClientId: "40r4p9q1l93gnac9q4g9t5eb6t",
    identityPoolId: "us-west-2:2bb776a7-932c-4c24-bbe2-3051771ef6a0",
    oauth: {
      domain: "federate-digigemba-beta.auth.us-west-2.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: "http://localhost:3000",
      redirectSignOut: "http://localhost:3000",
      responseType: "code",
    },
  },
  beta: {
    region: "us-west-2",
    userPoolId: "us-west-2_eJNPRTj1y",
    userPoolWebClientId: "40r4p9q1l93gnac9q4g9t5eb6t",
    identityPoolId: "us-west-2:2bb776a7-932c-4c24-bbe2-3051771ef6a0",
    oauth: {
      domain: "federate-digigemba-beta.auth.us-west-2.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: "https://beta.digigemba.app.amazon.dev",
      redirectSignOut: "https://beta.digigemba.app.amazon.dev",
      responseType: "code",
    },
  },
  prod: {
    region: "us-west-2",
    userPoolId: "us-west-2_ZgPMQ8iCV",
    userPoolWebClientId: "23uamopjn2smgl3uhir06o8cgf",
    identityPoolId: "us-west-2:82493585-7b00-4366-ad52-7ce87807bcb5",
    oauth: {
      domain: "federate-digigemba-prod.auth.us-west-2.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: "https://digigemba.app.amazon.dev",
      redirectSignOut: "https://digigemba.app.amazon.dev",
      responseType: "code",
    },
  },
};

const appSyncConfigs: Record<string, AppSyncConfig> = {
  beta: {
    endpoint:
      "https://p5te6ulk7fbftly4jp3oogbphy.appsync-api.us-west-2.amazonaws.com/graphql",
    region: "us-west-2",
    defaultAuthMode: "AMAZON_COGNITO_USER_POOLS",
  },
  prod: {
    endpoint:
      "https://pkxhd2ynmzacrc5h6gpr6emmky.appsync-api.us-west-2.amazonaws.com/graphql",
    region: "us-west-2",
    defaultAuthMode: "AMAZON_COGNITO_USER_POOLS",
  },
};

interface EnvConfig {
  authConfig: AuthConfig;
  appSyncConfig: AppSyncConfig;
}

export const envContext = createContext<EnvConfig>({
  authConfig: authConfigs.localhost,
  appSyncConfig: appSyncConfigs.localhost,
});

export const EnvProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  console.log("🌍 EnvProvider initializing");
  const [hostname] = useState(() => 
    typeof window !== "undefined" ? window.location.hostname : ""
  );
  
  const envConfig = useMemo<EnvConfig>(() => {
    let authConfig: AuthConfig;
    let appSyncConfig: AppSyncConfig;
    console.log(hostname);
    if (hostname.startsWith("localhost")) {
      authConfig = authConfigs.localhost;
      console.log("authConfig", authConfig);
      appSyncConfig = appSyncConfigs.beta;
      console.log("appSyncConfig", appSyncConfig);
    } else if (hostname.startsWith("beta.")) {
      authConfig = authConfigs.beta;
      appSyncConfig = appSyncConfigs.beta;
    } else {
      authConfig = authConfigs.prod;
      appSyncConfig = appSyncConfigs.prod;
    }

    return {
      authConfig,
      appSyncConfig,
    };
  }, [hostname]);

  useEffect(() => {
    const configure = async () => {
      try {
        Auth.configure({
          Auth: {
            region: envConfig.authConfig.region,
            userPoolId: envConfig.authConfig.userPoolId,
            userPoolWebClientId: envConfig.authConfig.userPoolWebClientId,
            identityPoolId: envConfig.authConfig.identityPoolId,
          },
          oauth: {
            domain: envConfig.authConfig.oauth.domain,
            scope: envConfig.authConfig.oauth.scope,
            redirectSignIn: envConfig.authConfig.oauth.redirectSignIn,
            redirectSignOut: envConfig.authConfig.oauth.redirectSignOut,
            responseType: envConfig.authConfig.oauth.responseType,
          },
          API: {
            GraphQL: {
              endpoint: envConfig.appSyncConfig.endpoint,
              region: envConfig.appSyncConfig.region,
              defaultAuthMode: envConfig.appSyncConfig.defaultAuthMode,
            },
          },
        });
        API.configure({
          aws_appsync_graphqlEndpoint: envConfig.appSyncConfig.endpoint,
          aws_appsync_region: envConfig.appSyncConfig.region,
          aws_appsync_authenticationType:
            envConfig.appSyncConfig.defaultAuthMode,
        });
      } catch (error) {
        console.error("Error configuring Auth:", error);
      }
    };

    configure();
  }, [envConfig]);

  return (
    <envContext.Provider value={envConfig}>{children}</envContext.Provider>
  );
};

export const useEnv = (): EnvConfig => useContext(envContext);
